/* eslint-disable react-hooks/exhaustive-deps */
import { KTSVG } from '../../_metronic/helpers';
import React, { useState, useEffect, useRef } from 'react';
import { Table } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Link, useHistory, useParams } from 'react-router-dom';
import validationErrorMsg from '../helpers/ValidationErrorMsg';
import { clampToInventoryDateBounds, getInventoryDateBounds, inventoryDateError, inventoryDateHint } from '../helpers/inventoryDateBounds';

const ITEM_TYPE_OPTIONS = [
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'finished_product', label: 'Finished Product' },
];

const SEARCH_LIMIT = 40;
const STOCK_VALUE_ADJUSTMENT_TYPE_ID = 11;

interface AdjustmentRow {
    idItems: number;
    idProducts: number;
    itemName: string;
    itemCode: string;
    categoryName: string;
    unitName: string;
    stockQuantity: number;
    physicalQuantity: number;
    averageRate: number;
    [key: string]: any;
}

interface RouteParams {
    idItemStockAdjustments: string;
}

const rowKey = (row: { idItems?: number; idProducts?: number }, finishedProduct: boolean) =>
    finishedProduct ? `p:${row.idProducts}` : `i:${row.idItems}`;

const getAdjQty = (row: AdjustmentRow) => {
    const phys = Number(row.physicalQuantity) || 0;
    const prev = Number(row.stockQuantity) || 0;
    return parseFloat((phys - prev).toFixed(2));
};

const mapOptionToRow = (option: any): AdjustmentRow => ({
    idItems: option.idItems || 0,
    idProducts: option.idProducts || 0,
    itemName: option.itemName,
    itemCode: option.itemCode || '',
    categoryName: option.categoryName ?? '',
    unitName: option.unitName || '',
    stockQuantity: option.stockQuantity ?? 0,
    physicalQuantity: option.physicalQuantity ?? option.stockQuantity ?? 0,
    averageRate: option.averageRate ?? 0,
});

const ItemStockAdjustmentEdit = () => {
    const history = useHistory();
    const { idItemStockAdjustments }: RouteParams = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [recalculating, setRecalculating] = useState(false);

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState<any>({ label: 'Select Project', value: '' });
    const [warehouses, setWarehouses] = useState<any[]>([]);
    const [warehouse, setWarehouse] = useState<any>({ label: 'Select Warehouse', value: '' });
    const [adjustmentTypes, setAdjustmentTypes] = useState<any[]>([]);
    const [adjustmentType, setAdjustmentType] = useState<any>({ label: 'Select Adjustment Type', value: '' });
    const [itemType, setItemType] = useState<any>(ITEM_TYPE_OPTIONS[0]);

    const [adjustment, setAdjustment] = useState({
        idProjects: 0,
        idWarehouses: 0,
        idItemStockAdjustmentType: 0,
        itemType: 'raw_material',
        adjustmentDate: '',
        remarks: '',
    });
    const [adjustmentRows, setAdjustmentRows] = useState<AdjustmentRow[]>([]);
    const [addSelectKey, setAddSelectKey] = useState(0);
    const itemSearchInputRef = useRef('');

    const isFinishedProduct = itemType?.value === 'finished_product';
    const isStockValueAdjustment = Number(adjustment.idItemStockAdjustmentType) === STOCK_VALUE_ADJUSTMENT_TYPE_ID;
    const detailsTitle = isFinishedProduct ? 'Product Details' : 'Material Details';
    const nameColumnLabel = isFinishedProduct ? 'Name of Products' : 'Name of Materials';
    const codeColumnLabel = isFinishedProduct ? 'Product Code' : 'Item Code';
    const searchPlaceholder = isFinishedProduct
        ? 'Search & add product by name or code...'
        : 'Search & add item by name or code...';
    const canSearchItems = !!project?.value && !!warehouse?.value && !!adjustment.adjustmentDate;

    useEffect(() => {
        Promise.all([getProjects(), getAdjustmentTypes()]).then(() => loadEditData());
    }, [idItemStockAdjustments]);

    const getProjects = () => {
        return fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.status && resp.data) setProjects(resp.data);
                return resp.data || [];
            })
            .catch(() => []);
    };

    const getWarehousesByProject = (idProjects: number, selectedWarehouseId?: number) => {
        if (!idProjects) {
            setWarehouses([]);
            return Promise.resolve([]);
        }
        return fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/warehouses/' + idProjects, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const list = resp.status && resp.data ? resp.data : [];
                setWarehouses(list);
                if (selectedWarehouseId) {
                    const found = list.find((w: any) => Number(w.value) === Number(selectedWarehouseId));
                    if (found) setWarehouse(found);
                }
                return list;
            })
            .catch(() => {
                setWarehouses([]);
                return [];
            });
    };

    const getAdjustmentTypes = () => {
        return fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/adjustment-types', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.status && resp.data) setAdjustmentTypes(resp.data);
                return resp.data || [];
            })
            .catch(() => []);
    };

    const loadEditData = () => {
        setLoading(true);
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/' + idItemStockAdjustments + '/edit', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (!resp.success || !resp.data) {
                    Swal.fire({ icon: 'error', title: 'Error', text: resp.errorMessage || 'Failed to load adjustment.' })
                        .then(() => history.push('/item-stock-adjustment'));
                    return;
                }
                const data = resp.data;
                const selectedItemType = ITEM_TYPE_OPTIONS.find((o) => o.value === data.itemType) || ITEM_TYPE_OPTIONS[0];
                setItemType(selectedItemType);
                setAdjustment({
                    idProjects: data.idProjects,
                    idWarehouses: data.idWarehouses,
                    idItemStockAdjustmentType: data.idItemStockAdjustmentType,
                    itemType: data.itemType,
                    adjustmentDate: data.adjustmentDate,
                    remarks: data.remarks || '',
                });
                setProject({
                    value: data.idProjects,
                    label: data.projectName,
                    inventoryBackdateDays: data.inventoryBackdateDays,
                });
                setAdjustmentType({
                    value: data.idItemStockAdjustmentType,
                    label: data.adjustmentTypeName || 'Select Adjustment Type',
                });
                setWarehouse({ value: data.idWarehouses, label: data.warehouseName || 'Select Warehouse' });
                setAdjustmentRows((data.rows || []).map(mapOptionToRow));
                getWarehousesByProject(data.idProjects, data.idWarehouses);
            })
            .catch(() => {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load adjustment.' })
                    .then(() => history.push('/item-stock-adjustment'));
            })
            .finally(() => setLoading(false));
    };

    const fetchStockList = (opts: {
        search?: string;
        idItems?: number[];
        idProducts?: number[];
        idWarehouses?: number | string;
        adjustmentDate?: string;
        adjustmentTypeId?: number | string | null;
    }): Promise<any[]> => {
        const params = new URLSearchParams({
            id_projects: String(project.value || adjustment.idProjects),
            id_warehouses: String(opts.idWarehouses ?? warehouse.value ?? adjustment.idWarehouses),
            adjustment_date: opts.adjustmentDate || adjustment.adjustmentDate,
            exclude_adjustment_id: String(idItemStockAdjustments),
            search: opts.search || '',
            limit: String(SEARCH_LIMIT),
            page: '1',
        });
        if (opts.idItems?.length) params.set('id_items', opts.idItems.join(','));
        if (opts.idProducts?.length) params.set('id_products', opts.idProducts.join(','));
        const typeId = opts.adjustmentTypeId ?? adjustment.idItemStockAdjustmentType;
        if (typeId) params.set('id_item_stock_adjustment_type', String(typeId));

        const endpoint = isFinishedProduct
            ? 'item-stock-adjustment/products-with-stock?'
            : 'item-stock-adjustment/items-with-stock?';

        return fetch(process.env.REACT_APP_API_URL + endpoint + params, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => (resp.status && resp.data ? resp.data : []))
            .catch(() => []);
    };

    const recalculatePrevStock = (ctx?: {
        warehouseValue?: any;
        adjustmentDate?: string;
        adjustmentTypeId?: number | string | null;
        refreshRates?: boolean;
    }) => {
        if (!adjustmentRows.length) return;
        const warehouseValue = ctx?.warehouseValue ?? warehouse?.value;
        const adjustmentDate = ctx?.adjustmentDate ?? adjustment.adjustmentDate;
        const adjustmentTypeId = ctx?.adjustmentTypeId ?? adjustment.idItemStockAdjustmentType;
        const refreshRates = ctx?.refreshRates === true
            || Number(adjustmentTypeId) === STOCK_VALUE_ADJUSTMENT_TYPE_ID;
        if (!warehouseValue || !adjustmentDate) return;

        setRecalculating(true);
        fetchStockList({
            idWarehouses: warehouseValue,
            adjustmentDate,
            adjustmentTypeId,
            idItems: isFinishedProduct ? undefined : adjustmentRows.map((r) => r.idItems).filter(Boolean),
            idProducts: isFinishedProduct ? adjustmentRows.map((r) => r.idProducts).filter(Boolean) : undefined,
        })
            .then((data) => {
                const stockByKey: Record<string, { stock: number; rate: number }> = {};
                data.forEach((opt: any) => {
                    stockByKey[rowKey(opt, isFinishedProduct)] = {
                        stock: Number(opt.stockQuantity ?? 0),
                        rate: Number(opt.averageRate ?? 0),
                    };
                });
                setAdjustmentRows((prev) =>
                    prev.map((row) => {
                        const key = rowKey(row, isFinishedProduct);
                        const matched = stockByKey[key];
                        if (!matched) return row;
                        return {
                            ...row,
                            stockQuantity: matched.stock,
                            ...(refreshRates ? { averageRate: matched.rate } : {}),
                        };
                    })
                );
            })
            .finally(() => setRecalculating(false));
    };

    const handleWarehouseChange = (selected: any) => {
        setWarehouse(selected);
        setAdjustment((prev) => ({ ...prev, idWarehouses: selected?.value || 0 }));
        if (selected?.value && adjustment.adjustmentDate) {
            recalculatePrevStock({ warehouseValue: selected.value });
        }
    };

    const handleAdjustmentTypeChange = (selected: any) => {
        setAdjustmentType(selected);
        const typeId = selected?.value || 0;
        setAdjustment((prev) => ({ ...prev, idItemStockAdjustmentType: typeId }));
        if (adjustmentRows.length && warehouse?.value && adjustment.adjustmentDate) {
            recalculatePrevStock({
                adjustmentTypeId: typeId,
                refreshRates: true,
            });
        }
    };

    const loadItemOptions = (inputValue: string): Promise<any[]> => {
        if (!canSearchItems) return Promise.resolve([]);
        const searchTerm = (inputValue || itemSearchInputRef.current || '').trim();
        if (!searchTerm) return Promise.resolve([]);

        return fetchStockList({ search: searchTerm }).then((data) => {
            const existing = new Set(adjustmentRows.map((r) => rowKey(r, isFinishedProduct)));
            return data.filter((opt: any) => !existing.has(rowKey(opt, isFinishedProduct)));
        });
    };

    const handleAddItem = (option: any) => {
        if (!option) return;
        const key = rowKey(option, isFinishedProduct);
        if (adjustmentRows.some((r) => rowKey(r, isFinishedProduct) === key)) {
            Swal.fire({ icon: 'info', title: 'Already added', text: 'Already in the list.', timer: 1400, showConfirmButton: false });
            setAddSelectKey((k) => k + 1);
            return;
        }
        setAdjustmentRows((prev) => [...prev, mapOptionToRow(option)]);
        setAddSelectKey((k) => k + 1);
    };

    const updateRowField = (index: number, field: 'physicalQuantity' | 'averageRate', value: number) => {
        setAdjustmentRows((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const removeRow = (index: number) => {
        setAdjustmentRows((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!adjustment.idProjects) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Project.' });
            return;
        }
        if (!adjustment.idWarehouses) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Warehouse.' });
            return;
        }
        if (!adjustment.idItemStockAdjustmentType) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Adjustment Type.' });
            return;
        }
        const dateError = inventoryDateError(adjustment.adjustmentDate, project?.inventoryBackdateDays);
        if (dateError) {
            Swal.fire({ icon: 'error', title: 'Error', text: dateError });
            return;
        }
        if (adjustmentRows.length === 0) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'At least one row is required.' });
            return;
        }

        const confirm = await Swal.fire({
            title: 'Update adjustment?',
            html: `Save <b>${adjustmentRows.length}</b> row(s) and recreate stock movements?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3699FF',
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
        });
        if (!confirm.isConfirmed) return;

        setSaving(true);
        const postData = {
            adjustment: {
                idProjects: adjustment.idProjects,
                idWarehouses: adjustment.idWarehouses,
                idItemStockAdjustmentType: adjustment.idItemStockAdjustmentType,
                itemType: adjustment.itemType,
                adjustmentDate: adjustment.adjustmentDate,
                remarks: adjustment.remarks || null,
            },
            adjustmentRows: adjustmentRows.map((r) => ({
                idItems: r.idItems || null,
                idProducts: isFinishedProduct ? r.idProducts : null,
                stockQuantity: isNaN(Number(r.stockQuantity)) ? 0 : Number(r.stockQuantity),
                physicalQuantity: Number(r.physicalQuantity) >= 0 ? Number(r.physicalQuantity) : 0,
                averageRate: isStockValueAdjustment
                    ? (isNaN(Number(r.averageRate)) ? 0 : Number(r.averageRate))
                    : (Number(r.averageRate) >= 0 ? Number(r.averageRate) : 0),
            })),
        };

        try {
            const res = await fetch(
                process.env.REACT_APP_API_URL + 'item-stock-adjustment/update/' + idItemStockAdjustments,
                postRequestOptions(postData)
            );
            const data = await res.json();
            if (!data.success) {
                const msg = validationErrorMsg(data.errors).join('<br>') || data.errorMessage || 'Failed to update.';
                Swal.fire({ icon: 'error', title: 'Error', html: msg });
                return;
            }
            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: data.successMessage || 'Stock adjustment updated.',
            });
            history.push('/item-stock-adjustment-details/' + idItemStockAdjustments);
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Request failed.' });
        } finally {
            setSaving(false);
        }
    };

    const dateBounds = getInventoryDateBounds(project?.inventoryBackdateDays);

    if (loading) {
        return (
            <div className="card card-custom">
                <div className="card-body text-center py-10">Loading adjustment...</div>
            </div>
        );
    }

    return (
        <div className="card card-custom">
            <div className="card-header">
                <div className="card-title">
                    <KTSVG path='/media/svg/icons/Design/Edit.svg' className='svg-icon-1 svg-icon-primary' />
                    <span className="text fw-bolder" style={{ marginLeft: '10px' }}>
                        Edit Stock Adjustment #{idItemStockAdjustments}
                    </span>
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Item Type <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={itemType} options={ITEM_TYPE_OPTIONS} isDisabled menuPosition="fixed" />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Project <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={project} options={projects} isDisabled menuPosition="fixed" />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Warehouse <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={warehouse} onChange={handleWarehouseChange} options={warehouses} placeholder="Select Warehouse" menuPosition="fixed" styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Date <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={adjustment.adjustmentDate}
                                min={dateBounds.minDate || undefined}
                                max={dateBounds.maxDate}
                                onChange={(e) => {
                                    const dateVal = clampToInventoryDateBounds(e.target.value, project?.inventoryBackdateDays);
                                    setAdjustment((p) => ({ ...p, adjustmentDate: dateVal }));
                                    if (warehouse?.value && dateVal) {
                                        recalculatePrevStock({ adjustmentDate: dateVal });
                                    }
                                }}
                                required
                            />
                            <div className="form-text text-muted mt-1">
                                {inventoryDateHint(!!project?.value, project?.inventoryBackdateDays)}
                                {recalculating ? ' Recalculating Prev. Stock...' : ' Prev. Stock excludes this adjustment and dates before inventory startup.'}
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Adjustment Type <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={adjustmentType} onChange={handleAdjustmentTypeChange} options={adjustmentTypes} placeholder="Select Adjustment Type" menuPosition="fixed" styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Remarks</label>
                        <div className="col-lg-4">
                            <textarea className="form-control form-control-sm" rows={2} value={adjustment.remarks} onChange={(e) => setAdjustment((p) => ({ ...p, remarks: e.target.value }))} />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Add {isFinishedProduct ? 'Product' : 'Item'}</label>
                        <div className="col-lg-6">
                            <AsyncSelect
                                key={`add-${addSelectKey}-${itemType?.value}`}
                                cacheOptions={false}
                                loadOptions={loadItemOptions}
                                onInputChange={(val) => { itemSearchInputRef.current = val ?? ''; }}
                                onChange={handleAddItem}
                                value={null}
                                placeholder={searchPlaceholder}
                                menuPosition="fixed"
                                styles={{ control: (p: any) => ({ ...p, minHeight: '36px' }), menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }}
                                menuPortalTarget={document.body}
                                isDisabled={!canSearchItems}
                            />
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">
                            {detailsTitle}
                            {recalculating && <span className="text-muted ms-2 fs-7 fw-normal">Updating Prev. Stock...</span>}
                        </h6>
                    </div>

                    <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                        <Table size="sm" bordered hover className="mb-0">
                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '4%' }} className="text-center">SL</th>
                                    <th style={{ width: '24%' }}>{nameColumnLabel}</th>
                                    <th style={{ width: '12%' }}>{codeColumnLabel}</th>
                                    <th style={{ width: '12%' }}>Category</th>
                                    <th style={{ width: '8%' }}>Unit</th>
                                    <th style={{ width: '10%' }}>Avg. Rate</th>
                                    <th style={{ width: '10%' }}>Prev. Stock</th>
                                    <th style={{ width: '10%' }}>Physical Qty</th>
                                    <th style={{ width: '10%' }}>Adjustment Qty</th>
                                    <th style={{ width: '6%' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adjustmentRows.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="text-center text-muted py-4">
                                            No rows. Search and add items/products.
                                        </td>
                                    </tr>
                                )}
                                {adjustmentRows.map((row, index) => {
                                    const adjQty = getAdjQty(row);
                                    return (
                                        <tr key={rowKey(row, isFinishedProduct)}>
                                            <td className="text-center">{index + 1}</td>
                                            <td><div className="fw-semibold text-dark" title={row.itemName}>{row.itemName}</div></td>
                                            <td>{row.itemCode || '-'}</td>
                                            <td>{row.categoryName || '-'}</td>
                                            <td>{row.unitName || '-'}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={row.averageRate === 0 || row.averageRate ? row.averageRate : ''}
                                                    onChange={(e) => {
                                                        const num = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                        updateRowField(index, 'averageRate', isNaN(num) ? 0 : (isStockValueAdjustment ? num : Math.max(0, num)));
                                                    }}
                                                    min={isStockValueAdjustment ? undefined : 0}
                                                    step="any"
                                                />
                                            </td>
                                            <td className="text-end">{row.stockQuantity}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={row.physicalQuantity === 0 || row.physicalQuantity ? row.physicalQuantity : ''}
                                                    onChange={(e) => {
                                                        const num = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                        updateRowField(index, 'physicalQuantity', isNaN(num) ? 0 : Math.max(0, num));
                                                    }}
                                                    min={0}
                                                    step="any"
                                                />
                                            </td>
                                            <td>
                                                <span className={`fw-bold ${adjQty > 0 ? 'text-success' : adjQty < 0 ? 'text-danger' : 'text-muted'}`}>
                                                    {adjQty > 0 ? '+' : ''}{adjQty}
                                                </span>
                                            </td>
                                            <td>
                                                <button type="button" className="btn btn-light-danger btn-sm" onClick={() => removeRow(index)}>
                                                    <KTSVG path='/media/svg/icons/General/Trash.svg' className='svg-icon-5' />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                        <button type="submit" className="btn btn-primary btn-sm me-2" disabled={saving || recalculating || adjustmentRows.length === 0}>
                            {saving ? 'Updating...' : 'Update Adjustment'}
                        </button>
                        <Link to="/item-stock-adjustment" className="btn btn-secondary btn-sm">Cancel</Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ItemStockAdjustmentEdit;
